import { useMemo } from 'react'
import { useScheduleProductionRequirements } from './Scheduler.data'

/**
 * Packaging routes are keyed by PRODUCT GROUP: a route row's `productCode`
 * holds a group code, and a product takes the routes of the group its Product
 * Master row names (`productGroup`; blank = the product's own code).
 *
 * This is the Portal's copy of the engine's `product_groups.rs`, for the
 * screens that ask "which routes can make this product?" — they must agree
 * with what a run will do. It returns ordinary per-product rows, so a caller
 * keeps matching on `productCode` as before.
 *
 * Read-only views only. An expanded row is a copy that shares its source
 * row's `id`, so it must never be saved back.
 */

type MasterRow = { productCode?: unknown; productGroup?: unknown }

const text = (v: unknown): string => {
  if (v === null || v === undefined) return ''
  const s = String(v).trim()
  return s.toLowerCase() === 'null' || s.toLowerCase() === 'none' ? '' : s
}

/** The product group a master row puts its product in. */
export const productGroupOf = (row: MasterRow): string =>
  text(row.productGroup) || text(row.productCode)

export function routesByProduct<R extends { productCode?: unknown }>(
  routes: R[] | undefined,
  master: MasterRow[] | undefined
): R[] {
  const rows = routes ?? []
  // product -> group, only where the group is not the product's own code.
  const explicit = new Map<string, string>()
  const members = new Map<string, string[]>()
  for (const m of master ?? []) {
    const code = text(m.productCode)
    if (!code) continue
    const group = productGroupOf(m)
    if (group !== code) explicit.set(code, group)
    const list = members.get(group) ?? []
    if (!list.includes(code)) list.push(code)
    members.set(group, list)
  }
  // No product names another group: every route means what it always did.
  if (explicit.size === 0) return rows

  const out: R[] = []
  for (const r of rows) {
    const key = text(r.productCode)
    const ms = key ? members.get(key) : undefined
    if (ms) {
      for (const m of ms) out.push({ ...r, productCode: m })
    } else if (key && explicit.has(key)) {
      // Keyed by a product that has moved into another group: the run
      // ignores this row (PRODUCT_GROUP_ROUTE_IGNORED), so it serves nobody.
      continue
    } else {
      out.push(r)
    }
  }
  return out
}

/** Product Master rows for one iteration, fetched only when `enabled`. Shares
 *  the React Query cache entry the Product Master tab uses. */
export const useProductMaster = (
  schedulerId: string,
  iteration: string,
  enabled = true
): MasterRow[] | undefined => {
  const { data } = useScheduleProductionRequirements(
    schedulerId,
    'product-master-data',
    iteration,
    undefined,
    enabled
  )
  return useMemo(() => data?.data as MasterRow[] | undefined, [data])
}
